/** @format */

import React, {useContext, useEffect, useState} from 'react';

import {Banner, GridSelect, TextLink, Button} from '@sttark/design-system';

import {QuoteContext} from 'frontier-quoter';
import withQuoteDataHandler from './hoc/with_quote_data_handler';

import {
    CARTON_BRIGHT_WHITE_PAPER,
    ORDER_STATUS_IN_CART,
    PRODUCT_TYPE_CARTON,
    PRODUCT_TYPE_GRAPHIC_DESIGN,
    PRODUCT_TYPE_LABEL
} from '../../utilities/constants';
import CartonQuoter from './carton_quoter';
import LabelQuoter from './label_quoter';
import DesignQuoterContainer from './design_quoter/design_quoter_container';
import {Header} from './header/header';
import ModalPortal from 'frontier-admin/modal/container_portal';

import switcherDesigns from './assets/switcher-designs.webp';
import switcherLabels from './assets/switcher-labels.webp';
import switcherBoxes from './assets/switcher-boxes.webp';
import './order_summary.sass';
import {AddToCart} from './components/add_to_cart';

function Quoter(props) {
    const ctx = useContext(QuoteContext);
    const order = ctx.order;
    const lastOrder = ctx.lastOrder;

    const handleChangeProductType = v => {
        if (v === PRODUCT_TYPE_GRAPHIC_DESIGN) {
            window.open('/orders/custom-design', '_blank', 'noopener,noreferrer');
            return;
        }

        ctx.handleToggleProductType(ctx.getProductTypeSlug(v), order?.id);
        ctx.handleChange({
            order: {
                $merge: {
                    product_type_id: v,
                    substrate_id: v === PRODUCT_TYPE_CARTON ? CARTON_BRIGHT_WHITE_PAPER : null,
                    is_embossing: v === PRODUCT_TYPE_LABEL ? false : order.is_embossing,
                    laminate_id: null
                }
            }
        });
    };

    if (order.product_type_id === PRODUCT_TYPE_GRAPHIC_DESIGN) {
        return <DesignQuoterContainer {...props} />;
    }

    // alert users their order will be lost if leaving page
    useEffect(() => {
        window.addEventListener('beforeunload', alertUser);
        if (order.status_id === ORDER_STATUS_IN_CART)
            window.removeEventListener('beforeunload', alertUser);
        if (lastOrder && lastOrder.status_id === ORDER_STATUS_IN_CART)
            window.removeEventListener('beforeunload', alertUser);

        return () => {
            window.removeEventListener('beforeunload', alertUser);
        };
    }, [ctx]);

    const alertUser = e => {
        e.preventDefault();
        e.returnValue = '';
    };

    return (
        <div>
            {ctx.showAddedToCart && (
                <AddToCart
                    order={lastOrder}
                    handleClose={() => ctx.handleChange({showAddedToCart: {$set: false}})}
                />
            )}
            {ctx.showSavedToAccount && (
                <Banner>
                    Your quote has been saved.{' '}
                    <TextLink href="/account/orders/saved">View saved quotes</TextLink>
                </Banner>
            )}
            <div className="container mt-3 mb-8">
                <Header />
                <>
                    <h3 className="mt-3">Product Type</h3>
                    <div className="d-flex align-items-start justify-content-start flex-wrap">
                        <div className="w-100">
                            <GridSelect
                                ratio="21x9"
                                mobileCols={1}
                                options={[
                                    {
                                        label: 'Labels',
                                        value: PRODUCT_TYPE_LABEL,
                                        image: switcherLabels
                                    },
                                    {
                                        label: 'Cartons',
                                        value: PRODUCT_TYPE_CARTON,
                                        image: switcherBoxes
                                    },
                                    {
                                        label: 'Design',
                                        value: PRODUCT_TYPE_GRAPHIC_DESIGN,
                                        image: switcherDesigns
                                    }
                                ]}
                                value={order.product_type_id}
                                onChange={handleChangeProductType}
                            />
                        </div>

                        {/* product type description */}
                        <div className="w-100 mb-5 mt-0">
                            {order.product_type_id === PRODUCT_TYPE_LABEL && (
                                <>
                                    <h6>About Our Custom Printed Labels </h6>
                                    <p>
                                        We take pride in delivering high-quality labels that go
                                        beyond mere information conveyance. Our custom label
                                        printing expertise has proven to elevate brands and take
                                        businesses to new heights. We offer a wide range of label
                                        materials, including White Plastic, Metallic Plastic, White
                                        Vinyl, and more, allowing you to showcase your product with
                                        a touch of elegance and luxury. Be it Food & Beverage,
                                        Cosmetics, Candles, Supplements, or Lipbalm, our labels
                                        faithfully tell the story of your wonderful product and
                                        brand. With a focus on innovation and creativity, we help
                                        you stand out on the shelf and make a lasting impression.
                                        Partner with us for innovative, cost-effective, and
                                        eye-catching product packaging that leaves a mark on your
                                        customers.
                                    </p>
                                </>
                            )}

                            {order.product_type_id === PRODUCT_TYPE_CARTON && (
                                <>
                                    <h6>About Our Custom Printed Cartons</h6>
                                    <p>
                                        We understand that your product's first interaction with
                                        customers is crucial for making a lasting impression. That's
                                        why we offer top-notch custom folding cartons designed to
                                        enhance your brand's image and ensure a remarkable unboxing
                                        experience. With our significant experience in label-making,
                                        we bring expertise outside the box, quite literally. Choose
                                        from a variety of carton materials, including white paper,
                                        metallic, and rainbow holographic, all paired with
                                        attractive finishes like gloss, matte, linen, or soft touch
                                        matte. Our cartons cater to various industries, such as
                                        Supplements, Vapes, Coffee & Tea, CBD and more, ensuring
                                        that your products stand out on retail shelves. Let us help
                                        you create custom cartons that captivate your customers and
                                        match the exceptional quality of your products.
                                    </p>
                                </>
                            )}
                        </div>
                    </div>
                </>
                {order.product_type_id === PRODUCT_TYPE_LABEL && <LabelQuoter {...props} />}
                {order.product_type_id === PRODUCT_TYPE_CARTON && <CartonQuoter {...props} />}
            </div>
        </div>
    );
}

export default withQuoteDataHandler(Quoter);
